import { Injectable, inject } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IntegrationsApiService } from '../data/integrations-api.service';
import { CONNECTOR_KEY } from '../catalog/provider-catalog';
import { FacebookSdkLoader } from './facebook-sdk.loader';
import type { ConnectContext, ConnectorStrategy } from './connector-strategy';
import type { ConnectResult } from '../domain/connect-result';

/**
 * Meta Ads connection via Facebook Login for Business (config_id grants ads_read,
 * issuing a non-expiring system-user token). On success the backend discovers the
 * ad accounts; the user picks which to sync on the Mídia Paga page.
 */
@Injectable({ providedIn: 'root' })
export class MetaAdsConnector implements ConnectorStrategy {
  readonly key = CONNECTOR_KEY.metaAds;
  readonly introHint =
    'Vamos abrir o login do Facebook para Empresas e pedir acesso de leitura aos seus anúncios (ads_read). Depois você escolhe quais contas sincronizar.';
  readonly cancelledHint = 'Você fechou a janela do Facebook sem concluir.';

  private readonly sdkLoader = inject(FacebookSdkLoader);
  private readonly api = inject(IntegrationsApiService);

  start(_ctx: ConnectContext): Observable<ConnectResult> {
    return new Observable<ConnectResult>((subscriber) => {
      const stop$ = new Subject<void>();

      this.sdkLoader
        .load()
        .pipe(takeUntil(stop$))
        .subscribe({
          next: (fb) => {
            fb.login(
              (response) => {
                const code = response?.authResponse?.code;
                if (!code) {
                  subscriber.next({ kind: 'cancelled' });
                  subscriber.complete();
                  return;
                }
                this.api
                  .connectMetaAds({ authCode: code })
                  .pipe(takeUntil(stop$))
                  .subscribe({
                    next: (resp) => {
                      subscriber.next({ kind: 'success', integrationId: resp.integration.id });
                      subscriber.complete();
                    },
                    error: (err: { error?: { message?: string } }) => {
                      subscriber.next({
                        kind: 'error',
                        message: err?.error?.message ?? 'Falha ao conectar a Meta Ads.',
                        recoverable: true,
                      });
                      subscriber.complete();
                    },
                  });
              },
              {
                config_id: environment.metaAdsConfigId,
                response_type: 'code',
                override_default_response_type: true,
              },
            );
          },
          error: () => {
            subscriber.next({
              kind: 'error',
              message: 'Não foi possível carregar o SDK do Facebook.',
              recoverable: true,
            });
            subscriber.complete();
          },
        });

      return () => {
        stop$.next();
        stop$.complete();
      };
    });
  }
}
